import { SparkIteration, SparkJob } from '../types/sparkTypes';

export interface JobResultNavigation {
  label: string;
  action: () => Promise<void> | void;
}

export interface JobNavigationDeps {
  navigateToStep?: (stepTypeFqn: string) => void;
  openCreativeEditor?: (
    creativeFqn: string,
    iterationFqn?: string,
    initialTab?: 'details' | 'deepsights'
  ) => void;
  resolveIteration?: (iterationFqn: string) => Promise<SparkIteration | null>;
  resolveFeedbackIdForJob?: (iterationFqn: string, jobFqn: string) => Promise<string | null>;
  scrollToFeedback?: (feedbackId: string) => void;
}

function getInputIterationFqns(job: SparkJob): string[] {
  const actionArgs = job._action_args?.action_args ?? {};
  const iterationFqns = new Set<string>();

  if (typeof actionArgs.input_iteration_fqn === 'string') {
    iterationFqns.add(actionArgs.input_iteration_fqn);
  }

  if (Array.isArray(actionArgs.input_iteration_fqns)) {
    actionArgs.input_iteration_fqns.forEach((iterationFqn) => {
      if (typeof iterationFqn === 'string') {
        iterationFqns.add(iterationFqn);
      }
    });
  }

  return Array.from(iterationFqns);
}

async function resolveSavedIteration(job: SparkJob, deps: JobNavigationDeps): Promise<SparkIteration | null> {
  if (!deps.resolveIteration || !job._result?.saved_fqns?.length) {
    return null;
  }

  let fallbackIteration: SparkIteration | null = null;

  for (const savedFqn of job._result.saved_fqns) {
    const iteration = await deps.resolveIteration(savedFqn);
    if (!iteration) {
      continue;
    }

    if (!fallbackIteration) {
      fallbackIteration = iteration;
    }

    if (iteration._generating_job_fqn === job.fqn) {
      return iteration;
    }
  }

  return fallbackIteration;
}

async function resolveRelevantIteration(job: SparkJob, deps: JobNavigationDeps): Promise<SparkIteration | null> {
  const savedIteration = await resolveSavedIteration(job, deps);
  if (savedIteration) {
    return savedIteration;
  }

  if (!deps.resolveIteration) {
    return null;
  }

  for (const iterationFqn of getInputIterationFqns(job)) {
    const iteration = await deps.resolveIteration(iterationFqn);
    if (iteration) {
      return iteration;
    }
  }

  return null;
}

export function getJobResultNavigation(
  job: SparkJob,
  deps: JobNavigationDeps
): JobResultNavigation | null {
  if (job._status !== 'completed') {
    return null;
  }

  if (job._result_kind === 'creation') {
    if (!job._target_type_fqn || !deps.navigateToStep) {
      return null;
    }

    return {
      label: 'View on board',
      action: () => deps.navigateToStep?.(job._target_type_fqn!),
    };
  }

  if (!deps.openCreativeEditor || !deps.resolveIteration) {
    return null;
  }

  if (job._result_kind === 'media') {
    return {
      label: 'Open creative',
      action: async () => {
        const iteration = await resolveRelevantIteration(job, deps);
        if (!iteration?._creative_fqn) {
          return;
        }

        deps.openCreativeEditor?.(iteration._creative_fqn, iteration.fqn, 'details');
      },
    };
  }

  if (job._result_kind === 'iteration') {
    return {
      label: 'View iteration',
      action: async () => {
        const iteration = await resolveRelevantIteration(job, deps);
        if (!iteration?._creative_fqn) {
          return;
        }
        deps.openCreativeEditor?.(iteration._creative_fqn, iteration.fqn, 'details');
      },
    };
  }

  if (job._result_kind === 'evaluation') {
    return {
      label: 'View evaluation',
      action: async () => {
        const iteration = await resolveRelevantIteration(job, deps);
        if (!iteration?._creative_fqn) {
          return;
        }

        let feedbackId: string | null = null;
        if (deps.resolveFeedbackIdForJob) {
          feedbackId = await deps.resolveFeedbackIdForJob(iteration.fqn, job.fqn);
        }

        deps.openCreativeEditor?.(iteration._creative_fqn, iteration.fqn, 'deepsights');
        if (feedbackId) {
          deps.scrollToFeedback?.(feedbackId);
        }
      },
    };
  }

  return null;
}
